import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import PortalLayout from "@/layouts/PortalLayout";
import { api, formatApiErrorDetail } from "@/lib/api";
import { MapPin, Calendar as Cal, Plus, X, Archive, ArchiveRestore, Trash2, Copy, Edit3 } from "lucide-react";
import PublicLinkButton from "@/components/PublicLinkButton";
import { toast } from "sonner";
import ImageUpload from "@/components/ImageUpload";
import GooglePlacesInput from "@/components/GooglePlacesInput";
import { useAdminTable } from "@/lib/useAdminTable";
import { AdminTableToolbar, AdminTablePager } from "@/components/admin/AdminTableHelpers";
import { BulkActionsBar, BulkCheckbox } from "@/components/admin/BulkActionsBar";
import NotifyContactsModal from "@/components/admin/NotifyContactsModal";
import DuplicateModal from "@/components/admin/DuplicateModal";

const FORMATS = ["physical", "digital", "hybrid"];
const TYPES = ["Conference", "Webinar", "Networking Event", "Workshop", "Masterclass", "Panel Discussion", "Interview"];

export default function AdminEvents() {
  const table = useAdminTable("/admin/events", { sort: "date", order: "desc" });
  const events = table.items;
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    title: "", slug: "", description: "", long_description: "",
    format: "physical", type: "Conference",
    location: "", country: "France", city: "",
    date: "", end_date: "", image: "", gallery: [], max_participants: 100,
    pricing: { is_paid: false, currency: "eur" },
    tickets: [],
    animation_types: [],
    virtual_link: "", virtual_link_label: "", virtual_password: "",
    audience: "public",
  });
  const [newTicket, setNewTicket] = useState({ name: "", price: 0, description: "", role: "visitor" });
  const addTicket = () => {
    if (!newTicket.name.trim()) return;
    setForm({ ...form, tickets: [...form.tickets, { ...newTicket, id: crypto.randomUUID() }] });
    setNewTicket({ name: "", price: 0, description: "", role: "visitor" });
  };
  const removeTicket = (idx) => setForm({ ...form, tickets: form.tickets.filter((_, i) => i !== idx) });

  const ANIMATION_TYPES = [
    { id: "round_table", label: "Table ronde" },
    { id: "conference", label: "Conférence" },
    { id: "speed_meeting", label: "RDV 1:1 individuel" },
    { id: "workshop", label: "Atelier" },
    { id: "demo", label: "Démonstration" },
    { id: "cocktail", label: "Cocktail" },
    { id: "informal", label: "Échanges informels" },
  ];

  const toggleAnim = (id) => setForm({ ...form, animation_types: form.animation_types.includes(id) ? form.animation_types.filter((x) => x !== id) : [...form.animation_types, id] });
  const BACKEND = process.env.REACT_APP_BACKEND_URL;

  const load = () => table.reload();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {}, []);

  const archiveEvent = async (ev) => {
    if (!window.confirm(`Archiver "${ev.title}" ?`)) return;
    await api.post(`/admin/events/${ev.id}/archive`); toast.success("Événement archivé"); load();
  };
  const unarchiveEvent = async (ev) => {
    await api.post(`/admin/events/${ev.id}/unarchive`); toast.success("Restauré"); load();
  };
  const deleteEvent = async (ev) => {
    if (!window.confirm(`⚠️ SUPPRIMER DÉFINITIVEMENT "${ev.title}" ?\nCette action est IRRÉVERSIBLE.\n\nUtilisez "Archiver" pour juste le masquer.`)) return;
    await api.delete(`/admin/events/${ev.id}`); toast.success("Supprimé"); load();
  };

  // Bulk selection
  const [selected, setSelected] = useState(new Set());
  const [notifyOpen, setNotifyOpen] = useState(false);
  const toggleSel = (id) => { const ns = new Set(selected); if (ns.has(id)) ns.delete(id); else ns.add(id); setSelected(ns); };
  const selectAll = () => setSelected(new Set(events.map((e) => e.id)));
  const clearSel = () => setSelected(new Set());
  const bulkDelete = async () => {
    if (!window.confirm(`⚠️ SUPPRIMER DÉFINITIVEMENT ${selected.size} événement${selected.size > 1 ? "s" : ""} ?\nCette action est IRRÉVERSIBLE.`)) return;
    try {
      const r = await api.post("/admin/events/bulk-delete", { ids: Array.from(selected) });
      toast.success(`${r.data.deleted} événement${r.data.deleted > 1 ? "s" : ""} supprimé${r.data.deleted > 1 ? "s" : ""}`);
      clearSel();
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Erreur");
    }
  };

  const duplicateEvent = (ev) => setDupTarget(ev);
  const [dupTarget, setDupTarget] = useState(null);
  const doDuplicate = async ({ shift, count }) => {
    try {
      const r = await api.post(`/admin/events/${dupTarget.id}/duplicate`, { shift, count });
      toast.success(`${r.data.count} événement${r.data.count > 1 ? "s" : ""} dupliqué${r.data.count > 1 ? "s" : ""} en brouillon`);
      setDupTarget(null);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Erreur");
    }
  };

  const onTitle = (v) => {
    const slug = v.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "").replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");
    setForm({ ...form, title: v, slug });
  };

  const create = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...form, date: new Date(form.date).toISOString(), end_date: form.end_date ? new Date(form.end_date).toISOString() : null };
      await api.post("/admin/events", payload);
      toast.success("Événement créé !");
      setOpen(false);
      setForm({ ...form, title: "", slug: "", description: "", long_description: "", location: "", city: "", date: "", end_date: "", image: "", gallery: [], tickets: [] });
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const addGalleryImage = (url) => { if (url) setForm({ ...form, gallery: [...form.gallery, url] }); };
  const removeGalleryImage = (i) => setForm({ ...form, gallery: form.gallery.filter((_, idx) => idx !== i) });

  const [edit, setEdit] = useState(null);
  const [editTicket, setEditTicket] = useState({ name: "", price: 0, description: "", role: "visitor" });
  const addEditTicket = () => {
    if (!editTicket.name.trim()) return;
    setEdit({ ...edit, tickets: [...(edit.tickets || []), { ...editTicket, id: crypto.randomUUID() }] });
    setEditTicket({ name: "", price: 0, description: "", role: "visitor" });
  };
  const removeEditTicket = (idx) => setEdit({ ...edit, tickets: (edit.tickets || []).filter((_, i) => i !== idx) });

  const saveEdit = async () => {
    if (!edit) return;
    try {
      const payload = {
        title: edit.title, description: edit.description, long_description: edit.long_description,
        format: edit.format, type: edit.type, location: edit.location, country: edit.country, city: edit.city,
        date: edit.date, end_date: edit.end_date, image: edit.image, gallery: edit.gallery,
        max_participants: edit.max_participants, status: edit.status, animation_types: edit.animation_types || [],
        tickets: edit.tickets || [],
        pricing: edit.pricing || { is_paid: false, currency: "eur" },
        virtual_link: edit.virtual_link || "", virtual_link_label: edit.virtual_link_label || "", virtual_password: edit.virtual_password || "",
        audience: edit.audience || "public",
      };
      await api.patch(`/admin/events/${edit.id}`, payload);
      toast.success("Événement mis à jour");
      setEdit(null);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const STATUS_OPTIONS = ["draft", "published", "cancelled", "archived"];
  const FORMAT_OPTIONS = ["physical", "digital", "hybrid"];

  return (
    <PortalLayout sidebar="admin">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-3xl font-bold text-navy md:text-4xl">Événements</h1>
          <p className="mt-1 text-neutral-600">Créez et gérez les événements de la communauté.</p>
        </div>
        <button onClick={() => setOpen(!open)} className="inline-flex items-center gap-2 rounded-full bg-wine-500 px-5 py-2.5 text-sm font-semibold text-white hover:bg-wine-600" data-testid="admin-event-create-btn">
          {open ? <X className="h-4 w-4" /> : <Plus className="h-4 w-4" />} {open ? "Fermer" : "Nouvel événement"}
        </button>
      </div>

      {open && (
        <form onSubmit={create} className="mt-6 flex flex-col gap-4 rounded-2xl border border-wine-200 bg-white p-6" data-testid="admin-event-form">
          <input required value={form.title} onChange={(e) => onTitle(e.target.value)} placeholder="Titre de l'événement" className="rounded-xl border border-neutral-200 px-4 py-2.5 text-sm md:col-span-2" data-testid="event-create-title" />
          <input required value={form.slug} onChange={(e) => setForm({ ...form, slug: e.target.value })} placeholder="slug-url" className="rounded-xl border border-neutral-200 px-4 py-2.5 text-sm" data-testid="event-create-slug" />
          <select value={form.format} onChange={(e) => setForm({ ...form, format: e.target.value })} className="rounded-xl border border-neutral-200 px-4 py-2.5 text-sm" data-testid="event-create-format">{FORMATS.map((f) => <option key={f}>{f}</option>)}</select>
          <select value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })} className="rounded-xl border border-neutral-200 px-4 py-2.5 text-sm" data-testid="event-create-type">{TYPES.map((t) => <option key={t}>{t}</option>)}</select>
          <input required value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value, location: `${e.target.value}, ${form.country}` })} placeholder="Ville" className="rounded-xl border border-neutral-200 px-4 py-2.5 text-sm" data-testid="event-create-city" />
          <input required value={form.country} onChange={(e) => setForm({ ...form, country: e.target.value, location: `${form.city}, ${e.target.value}` })} placeholder="Pays" className="rounded-xl border border-neutral-200 px-4 py-2.5 text-sm" data-testid="event-create-country" />
          <div className="md:col-span-4">
            <GooglePlacesInput value={form.address || ""} placeholder="Adresse précise (Google Places) — facultatif" testId="event-create-address" onSelect={(p) => setForm({ ...form, address: p.address, location: p.address || form.location, city: p.city || form.city, country: p.country || form.country, google_place_id: p.place_id || "" })} />
          </div>
          <input required type="datetime-local" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className="rounded-xl border border-neutral-200 px-4 py-2.5 text-sm" data-testid="event-create-date" />
          <input type="datetime-local" value={form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })} className="rounded-xl border border-neutral-200 px-4 py-2.5 text-sm" data-testid="event-create-enddate" />
          <input type="number" value={form.max_participants} onChange={(e) => setForm({ ...form, max_participants: parseInt(e.target.value) || 100 })} className="rounded-xl border border-neutral-200 px-4 py-2.5 text-sm" data-testid="event-create-max" placeholder="Participants max" />
          <select value={form.audience} onChange={(e) => setForm({ ...form, audience: e.target.value })} className="rounded-xl border border-neutral-200 px-4 py-2.5 text-sm" data-testid="event-create-audience">
            <option value="public">🌐 Ouvert au public</option>
            <option value="members_only">🔒 Réservé aux membres</option>
          </select>

          {/* Virtual link block (visible for digital / hybrid) */}
          {(form.format === "digital" || form.format === "hybrid") && (
            <div className="md:col-span-2 rounded-2xl border-2 border-wine-300 bg-wine-50/40 p-4" data-testid="virtual-link-block-create">
              <div className="text-sm font-bold text-navy inline-flex items-center gap-1.5">🎥 Lien de visio</div>
              <p className="mt-1 text-[11px] text-neutral-600">Visible uniquement par les participants confirmés (et l'admin). Zoom, Teams, Google Meet…</p>
              <div className="mt-3 grid gap-2 md:grid-cols-2">
                <input type="url" value={form.virtual_link} onChange={(e) => setForm({ ...form, virtual_link: e.target.value })} placeholder="https://zoom.us/j/123456789" className="md:col-span-2 rounded-xl border border-wine-200 px-3 py-2 text-sm" data-testid="event-create-virtual-link" />
                <input value={form.virtual_link_label} onChange={(e) => setForm({ ...form, virtual_link_label: e.target.value })} placeholder="Nom (ex: Salle Zoom principale)" className="rounded-xl border border-wine-200 px-3 py-2 text-sm" />
                <input value={form.virtual_password} onChange={(e) => setForm({ ...form, virtual_password: e.target.value })} placeholder="Code d'accès (optionnel)" className="rounded-xl border border-wine-200 px-3 py-2 text-sm" />
              </div>
            </div>
          )}

          <div className="md:col-span-2">
            <ImageUpload value={form.image} onChange={(url) => setForm({ ...form, image: url })} label="Bannière principale" testId="event-image" />
          </div>
          <div className="md:col-span-2">
            <label className="text-xs font-semibold uppercase tracking-wider text-neutral-500">Galerie ({form.gallery.length} photo{form.gallery.length > 1 ? "s" : ""})</label>
            {form.gallery.length > 0 && (
              <div className="mt-2 grid grid-cols-3 gap-2 md:grid-cols-4">
                {form.gallery.map((url, i) => {
                  const src = url.startsWith("/api/") ? `${BACKEND}${url}` : url;
                  return (
                    <div key={i} className="relative">
                      <img src={src} alt="" className="aspect-square w-full rounded-lg object-cover" />
                      <button type="button" onClick={() => removeGalleryImage(i)} className="absolute top-1 right-1 rounded-full bg-white/90 p-1 text-neutral-700 hover:bg-white" data-testid={`event-gallery-remove-${i}`}><X className="h-3 w-3" /></button>
                    </div>
                  );
                })}
              </div>
            )}
            <div className="mt-2">
              <ImageUpload value="" onChange={addGalleryImage} label="Ajouter une photo à la galerie" testId="event-gallery-add" />
            </div>
          </div>
          <textarea required rows={2} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Description courte" className="rounded-xl border border-neutral-200 px-4 py-2.5 text-sm md:col-span-2" data-testid="event-create-desc" />
          <textarea rows={4} value={form.long_description} onChange={(e) => setForm({ ...form, long_description: e.target.value })} placeholder="Description longue" className="rounded-xl border border-neutral-200 px-4 py-2.5 text-sm md:col-span-2" data-testid="event-create-longdesc" />

          {/* Animation types block */}
          <div className="md:col-span-2 rounded-2xl border border-wine-100 bg-white p-5">
            <div className="font-bold text-navy">Types d'animations</div>
            <p className="mt-1 text-xs text-neutral-500">Sélectionnez tout ce qui se passera durant l'événement.</p>
            <div className="mt-3 flex flex-wrap gap-2">
              {ANIMATION_TYPES.map((a) => {
                const active = form.animation_types.includes(a.id);
                return (
                  <button
                    type="button"
                    key={a.id}
                    onClick={() => toggleAnim(a.id)}
                    className={`rounded-full border px-3.5 py-1.5 text-xs font-semibold transition-all ${active ? "border-wine-500 bg-wine-500 text-white" : "border-wine-200 bg-white text-wine-700 hover:bg-wine-50"}`}
                    data-testid={`event-anim-${a.id}`}
                  >
                    {a.label}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Tickets block — custom names + prices */}
          <div className="md:col-span-2 rounded-2xl border-2 border-dashed border-wine-200 bg-wine-50/30 p-5" data-testid="event-pricing-block">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <div className="font-bold text-navy">Tarification & billets</div>
                <p className="mt-0.5 text-xs text-neutral-600">Ajoutez autant de types de billets que nécessaire (ex. Early Bird, Standard, VIP, Exposant, Presse…). Laissez vide pour un événement gratuit.</p>
              </div>
              <label className="flex items-center gap-2 text-xs font-semibold text-neutral-700">
                Devise
                <select value={form.pricing.currency} onChange={(e) => setForm({ ...form, pricing: { ...form.pricing, currency: e.target.value } })} className="rounded-lg border border-neutral-200 px-2 py-1 text-sm" data-testid="price-currency">
                  <option value="eur">EUR (€)</option>
                  <option value="usd">USD ($)</option>
                  <option value="gbp">GBP (£)</option>
                </select>
              </label>
            </div>

            <div className="mt-4 grid gap-2 sm:grid-cols-5">
              <input placeholder="Nom du billet" value={newTicket.name} onChange={(e) => setNewTicket({ ...newTicket, name: e.target.value })} className="sm:col-span-2 rounded-xl border border-neutral-200 bg-white px-3 py-2 text-sm" data-testid="event-new-ticket-name" />
              <input type="number" min="0" step="0.01" placeholder="Prix" value={newTicket.price} onChange={(e) => setNewTicket({ ...newTicket, price: parseFloat(e.target.value) || 0 })} className="rounded-xl border border-neutral-200 bg-white px-3 py-2 text-sm" data-testid="event-new-ticket-price" />
              <select value={newTicket.role} onChange={(e) => setNewTicket({ ...newTicket, role: e.target.value })} className="rounded-xl border border-neutral-200 bg-white px-3 py-2 text-sm" data-testid="event-new-ticket-role">
                <option value="visitor">Visiteur</option>
                <option value="exhibitor">Exposant</option>
                <option value="media">Média</option>
              </select>
              <button type="button" onClick={addTicket} className="rounded-xl bg-wine-500 px-3 py-2 text-xs font-bold text-white hover:bg-wine-600" data-testid="event-new-ticket-add">+ Ajouter</button>
              <input placeholder="Description (optionnel)" value={newTicket.description} onChange={(e) => setNewTicket({ ...newTicket, description: e.target.value })} className="sm:col-span-5 rounded-xl border border-neutral-200 bg-white px-3 py-2 text-sm" />
            </div>

            <div className="mt-4 space-y-2">
              {form.tickets.length === 0 && <div className="rounded-lg bg-white px-3 py-3 text-center text-xs italic text-neutral-400">Aucun billet ajouté → l'événement sera gratuit.</div>}
              {form.tickets.map((t, idx) => (
                <div key={t.id || idx} className="flex items-center gap-3 rounded-xl border border-neutral-100 bg-white p-3" data-testid={`event-ticket-row-${idx}`}>
                  <div className="flex-1">
                    <div className="text-sm font-bold text-navy">{t.name} <span className="ml-2 rounded-full bg-neutral-100 px-2 py-0.5 text-[10px] font-normal text-neutral-500">{t.role}</span></div>
                    {t.description && <div className="text-xs text-neutral-600">{t.description}</div>}
                  </div>
                  <div className="font-bold text-wine-700">{t.price > 0 ? `${t.price} ${(form.pricing.currency || "eur").toUpperCase()}` : "Gratuit"}</div>
                  <button type="button" onClick={() => removeTicket(idx)} className="grid h-7 w-7 place-items-center rounded-full text-rose-500 hover:bg-rose-50" data-testid={`event-ticket-remove-${idx}`}><Trash2 className="h-3.5 w-3.5" /></button>
                </div>
              ))}
            </div>
          </div>

          <div className="md:col-span-2">
            <button type="submit" className="rounded-full bg-wine-500 px-6 py-2.5 text-sm font-semibold text-white hover:bg-wine-600" data-testid="event-create-submit">Publier l'événement</button>
          </div>
        </form>
      )}

      <div className="mt-6"><AdminTableToolbar
        table={table}
        placeholder="Rechercher titre, lieu, description…"
        filters={[
          { key: "status", label: "Tous statuts", options: [{ value: "published", label: "Publié" }, { value: "draft", label: "Brouillon" }, { value: "cancelled", label: "Annulé" }] },
          { key: "format", label: "Tous formats", options: [{ value: "physical", label: "Physique" }, { value: "digital", label: "Digital" }, { value: "hybrid", label: "Hybride" }] },
          { key: "when", label: "Toutes périodes", options: [{ value: "upcoming", label: "À venir" }, { value: "past", label: "Passés" }] },
        ]}
      /></div>

      {selected.size > 0 && (
        <div className="mt-3">
          <BulkActionsBar
            count={selected.size}
            total={events.length}
            onClear={clearSel}
            onSelectAll={selectAll}
            onDelete={bulkDelete}
            label="événement"
            testId="admin-events-bulk"
            extraActions={
              <button onClick={() => setNotifyOpen(true)} className="inline-flex items-center gap-1.5 rounded-full bg-wine-600 px-4 py-1.5 text-xs font-bold text-white hover:bg-wine-700" data-testid="events-bulk-notify">
                🔔 Notifier des contacts
              </button>
            }
          />
        </div>
      )}
      {notifyOpen && (
        <NotifyContactsModal
          open={notifyOpen}
          onClose={() => { setNotifyOpen(false); clearSel(); }}
          entityType="event"
          entityIds={Array.from(selected)}
          endpoint="/admin/events/notify-contacts-bulk"
          payloadKey="event_ids"
        />
      )}

      <div className="mt-4 grid gap-5 md:grid-cols-2 lg:grid-cols-3">
        {events.map((e) => {
          const src = e.image?.startsWith("/api/") ? `${BACKEND}${e.image}` : e.image;
          const isSelected = selected.has(e.id);
          return (
            <div key={e.id} className={`relative rounded-2xl border bg-white p-5 ${isSelected ? "border-rose-400 ring-2 ring-rose-300" : (e.archived ? "border-amber-300 opacity-70" : "border-neutral-100")}`} data-testid={`admin-event-${e.id}`}>
              <div className="absolute top-3 left-3 z-10">
                <BulkCheckbox checked={isSelected} onChange={() => toggleSel(e.id)} testId={`admin-event-select-${e.id}`} />
              </div>
              <Link to={`/admin/events/${e.id}`} className="block">
              {src && <img src={src} alt={e.title} className="aspect-[16/9] w-full rounded-xl object-cover" />}
              <h3 className="mt-3 text-lg font-bold text-navy hover:text-wine-700">{e.title}</h3>
              </Link>
              <div className="mt-2 flex flex-wrap gap-2 text-xs text-neutral-500">
                <span className="inline-flex items-center gap-1"><Cal className="h-3 w-3" />{new Date(e.date).toLocaleDateString("fr")}</span>
                <span className="inline-flex items-center gap-1"><MapPin className="h-3 w-3" />{e.location}</span>
              </div>
              <div className="mt-3 flex items-center justify-between text-xs">
                <span className="rounded-full bg-green-50 px-2 py-0.5 font-semibold text-green-700">{e.status}</span>
                {e.archived && <span className="rounded-full bg-amber-100 px-2 py-0.5 font-bold text-amber-800">ARCHIVÉ</span>}
                <span className="text-neutral-500">{e.registered || 0} / {e.max_participants}</span>
              </div>
              <div className="mt-3 flex flex-wrap items-center gap-2">
                <PublicLinkButton slug={e.slug} type="event" compact />
                <div className="ml-auto flex items-center gap-1">
                  <button onClick={() => setEdit({ ...e })} className="grid h-7 w-7 place-items-center rounded-full text-neutral-500 hover:bg-blue-50 hover:text-blue-700" title="Modifier" data-testid={`admin-event-edit-${e.id}`}><Edit3 className="h-3.5 w-3.5" /></button>
                  <button onClick={() => duplicateEvent(e)} className="grid h-7 w-7 place-items-center rounded-full text-neutral-500 hover:bg-blue-50 hover:text-blue-700" title="Dupliquer (brouillon)" data-testid={`admin-event-duplicate-${e.id}`}><Copy className="h-3.5 w-3.5" /></button>
                  {e.archived
                    ? <button onClick={() => unarchiveEvent(e)} className="grid h-7 w-7 place-items-center rounded-full text-neutral-500 hover:bg-emerald-50 hover:text-emerald-700" title="Restaurer" data-testid={`admin-event-unarchive-${e.id}`}><ArchiveRestore className="h-3.5 w-3.5" /></button>
                    : <button onClick={() => archiveEvent(e)} className="grid h-7 w-7 place-items-center rounded-full text-neutral-500 hover:bg-amber-50 hover:text-amber-700" title="Archiver" data-testid={`admin-event-archive-${e.id}`}><Archive className="h-3.5 w-3.5" /></button>}
                  <button onClick={() => deleteEvent(e)} className="grid h-7 w-7 place-items-center rounded-full text-neutral-500 hover:bg-red-50 hover:text-red-700" title="Supprimer" data-testid={`admin-event-delete-${e.id}`}><Trash2 className="h-3.5 w-3.5" /></button>
                </div>
              </div>
            </div>
          );
        })}
      </div>
      <div className="mt-4 rounded-2xl border border-neutral-100 bg-white"><AdminTablePager table={table} /></div>

      {dupTarget && (
        <DuplicateModal
          title={dupTarget.title}
          sourceDate={dupTarget.date}
          kind="event"
          onClose={() => setDupTarget(null)}
          onConfirm={doDuplicate}
        />
      )}

      {edit && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="event-edit-modal">
          <button onClick={() => setEdit(null)} className="absolute inset-0 bg-neutral-900/60 backdrop-blur-sm" aria-label="Fermer" />
          <div className="relative max-h-[90vh] w-full max-w-3xl overflow-y-auto rounded-3xl bg-white p-7 shadow-2xl">
            <div className="flex items-center justify-between border-b border-neutral-100 pb-3">
              <h2 className="text-xl font-bold text-navy">Modifier : {edit.title}</h2>
              <button onClick={() => setEdit(null)} className="grid h-9 w-9 place-items-center rounded-full text-neutral-500 hover:bg-neutral-100" data-testid="event-edit-close"><X className="h-5 w-5" /></button>
            </div>
            <div className="mt-5 flex flex-col gap-3">
              <input required value={edit.title} onChange={(e) => setEdit({ ...edit, title: e.target.value })} placeholder="Titre" className="md:col-span-2 rounded-xl border border-neutral-200 px-3 py-2 text-sm" data-testid="event-edit-title" />
              <select value={edit.status || "published"} onChange={(e) => setEdit({ ...edit, status: e.target.value })} className="rounded-xl border border-neutral-200 px-3 py-2 text-sm" data-testid="event-edit-status">
                {STATUS_OPTIONS.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
              <select value={edit.format} onChange={(e) => setEdit({ ...edit, format: e.target.value })} className="rounded-xl border border-neutral-200 px-3 py-2 text-sm">
                {FORMAT_OPTIONS.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
              <input value={edit.city} onChange={(e) => setEdit({ ...edit, city: e.target.value })} placeholder="Ville" className="rounded-xl border border-neutral-200 px-3 py-2 text-sm" />
              <input value={edit.country} onChange={(e) => setEdit({ ...edit, country: e.target.value })} placeholder="Pays" className="rounded-xl border border-neutral-200 px-3 py-2 text-sm" />
              <div className="md:col-span-2">
                <GooglePlacesInput value={edit.address || ""} placeholder="Adresse précise (Google Places)" testId="event-edit-address" onSelect={(p) => setEdit({ ...edit, address: p.address, location: p.address || edit.location, city: p.city || edit.city, country: p.country || edit.country, google_place_id: p.place_id || "" })} />
              </div>
              <input type="datetime-local" value={edit.date?.slice(0, 16) || ""} onChange={(e) => setEdit({ ...edit, date: e.target.value })} className="rounded-xl border border-neutral-200 px-3 py-2 text-sm" data-testid="event-edit-date" />
              <input type="number" min="1" value={edit.max_participants} onChange={(e) => setEdit({ ...edit, max_participants: parseInt(e.target.value) || 1 })} placeholder="Max participants" className="rounded-xl border border-neutral-200 px-3 py-2 text-sm" />
              <select value={edit.audience || "public"} onChange={(e) => setEdit({ ...edit, audience: e.target.value })} className="rounded-xl border border-neutral-200 px-3 py-2 text-sm md:col-span-2" data-testid="event-edit-audience">
                <option value="public">🌐 Ouvert au public</option>
                <option value="members_only">🔒 Réservé aux membres</option>
              </select>
              <textarea rows={2} value={edit.description || ""} onChange={(e) => setEdit({ ...edit, description: e.target.value })} placeholder="Description courte" className="md:col-span-2 rounded-xl border border-neutral-200 px-3 py-2 text-sm" />
              <textarea rows={4} value={edit.long_description || ""} onChange={(e) => setEdit({ ...edit, long_description: e.target.value })} placeholder="Description longue" className="md:col-span-2 rounded-xl border border-neutral-200 px-3 py-2 text-sm" />

              {/* Image principale + Galerie */}
              <div className="md:col-span-2 grid gap-4 md:grid-cols-2">
                <div>
                  <label className="text-xs font-semibold uppercase tracking-wider text-neutral-500">Bannière principale</label>
                  <div className="mt-2">
                    <ImageUpload value={edit.image || ""} onChange={(url) => setEdit({ ...edit, image: url })} label="Bannière principale" testId="event-edit-image" />
                  </div>
                </div>
                <div>
                  <label className="text-xs font-semibold uppercase tracking-wider text-neutral-500">Galerie ({(edit.gallery || []).length} photo{(edit.gallery || []).length > 1 ? "s" : ""})</label>
                  {(edit.gallery || []).length > 0 && (
                    <div className="mt-2 grid grid-cols-3 gap-2">
                      {(edit.gallery || []).map((url, i) => {
                        const src = url?.startsWith("/api/") ? `${BACKEND}${url}` : url;
                        return (
                          <div key={i} className="relative overflow-hidden rounded-lg border border-neutral-200">
                            <img src={src} alt="" className="aspect-square w-full object-cover" />
                            <button type="button" onClick={() => setEdit({ ...edit, gallery: (edit.gallery || []).filter((_, idx) => idx !== i) })} className="absolute top-1 right-1 rounded-full bg-white/90 p-1 text-neutral-700 hover:bg-white" data-testid={`event-edit-gallery-remove-${i}`}><X className="h-3 w-3" /></button>
                          </div>
                        );
                      })}
                    </div>
                  )}
                  <div className="mt-2">
                    <ImageUpload value="" onChange={(url) => url && setEdit({ ...edit, gallery: [...(edit.gallery || []), url] })} label="Ajouter une photo" testId="event-edit-gallery-add" />
                  </div>
                </div>
              </div>

              {/* Animation types */}
              <div className="md:col-span-2 rounded-xl border border-wine-100 bg-wine-50/30 p-3">
                <div className="text-xs font-bold text-navy">Types d'animations</div>
                <div className="mt-2 flex flex-wrap gap-1.5">
                  {ANIMATION_TYPES.map((a) => {
                    const active = (edit.animation_types || []).includes(a.id);
                    return (
                      <button type="button" key={a.id} onClick={() => setEdit({ ...edit, animation_types: active ? (edit.animation_types || []).filter((x) => x !== a.id) : [...(edit.animation_types || []), a.id] })} className={`rounded-full border px-3 py-1 text-[11px] font-semibold ${active ? "border-wine-500 bg-wine-500 text-white" : "border-wine-200 bg-white text-wine-700 hover:bg-wine-50"}`} data-testid={`event-edit-anim-${a.id}`}>{a.label}</button>
                    );
                  })}
                </div>
              </div>
              {/* Virtual link (digital / hybrid events) */}
              {(edit.format === "digital" || edit.format === "hybrid") && (
                <div className="md:col-span-2 rounded-xl border border-wine-200 bg-wine-50/40 p-3" data-testid="virtual-link-block">
                  <div className="text-xs font-bold text-navy inline-flex items-center gap-1.5">🎥 Lien de visio (visible uniquement par les participants confirmés)</div>
                  <div className="mt-2 grid gap-2 md:grid-cols-2">
                    <input type="url" value={edit.virtual_link || ""} onChange={(e) => setEdit({ ...edit, virtual_link: e.target.value })} placeholder="https://zoom.us/j/..." className="md:col-span-2 rounded-xl border border-wine-200 px-3 py-2 text-sm" data-testid="event-virtual-link" />
                    <input value={edit.virtual_link_label || ""} onChange={(e) => setEdit({ ...edit, virtual_link_label: e.target.value })} placeholder="Nom (ex: Salle Zoom principale)" className="rounded-xl border border-wine-200 px-3 py-2 text-sm" />
                    <input value={edit.virtual_password || ""} onChange={(e) => setEdit({ ...edit, virtual_password: e.target.value })} placeholder="Code d'accès (optionnel)" className="rounded-xl border border-wine-200 px-3 py-2 text-sm" />
                  </div>
                </div>
              )}

              {/* Tickets (custom names + prices) */}
              <div className="md:col-span-2 rounded-xl border-2 border-dashed border-wine-200 bg-wine-50/30 p-4" data-testid="event-edit-tickets-block">
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div>
                    <div className="text-sm font-bold text-navy">Tarification & billets</div>
                    <p className="mt-0.5 text-[11px] text-neutral-600">Ajoutez les billets pour cet événement. Laissez vide = gratuit.</p>
                  </div>
                  <label className="flex items-center gap-2 text-xs font-semibold text-neutral-700">
                    Devise
                    <select value={edit.pricing?.currency || "eur"} onChange={(e) => setEdit({ ...edit, pricing: { ...(edit.pricing || {}), currency: e.target.value } })} className="rounded-lg border border-neutral-200 px-2 py-1 text-sm" data-testid="event-edit-currency">
                      <option value="eur">EUR (€)</option>
                      <option value="usd">USD ($)</option>
                      <option value="gbp">GBP (£)</option>
                    </select>
                  </label>
                </div>
                <div className="mt-3 grid gap-2 sm:grid-cols-5">
                  <input placeholder="Nom du billet" value={editTicket.name} onChange={(e) => setEditTicket({ ...editTicket, name: e.target.value })} className="sm:col-span-2 rounded-xl border border-neutral-200 bg-white px-3 py-2 text-sm" data-testid="event-edit-new-ticket-name" />
                  <input type="number" min="0" step="0.01" placeholder="Prix" value={editTicket.price} onChange={(e) => setEditTicket({ ...editTicket, price: parseFloat(e.target.value) || 0 })} className="rounded-xl border border-neutral-200 bg-white px-3 py-2 text-sm" data-testid="event-edit-new-ticket-price" />
                  <select value={editTicket.role} onChange={(e) => setEditTicket({ ...editTicket, role: e.target.value })} className="rounded-xl border border-neutral-200 bg-white px-3 py-2 text-sm">
                    <option value="visitor">Visiteur</option>
                    <option value="exhibitor">Exposant</option>
                    <option value="media">Média</option>
                  </select>
                  <button type="button" onClick={addEditTicket} className="rounded-xl bg-wine-500 px-3 py-2 text-xs font-bold text-white hover:bg-wine-600" data-testid="event-edit-new-ticket-add">+ Ajouter</button>
                  <input placeholder="Description (optionnel)" value={editTicket.description} onChange={(e) => setEditTicket({ ...editTicket, description: e.target.value })} className="sm:col-span-5 rounded-xl border border-neutral-200 bg-white px-3 py-2 text-sm" />
                </div>
                <div className="mt-3 space-y-2">
                  {(edit.tickets || []).length === 0 && <div className="rounded-lg bg-white px-3 py-2.5 text-center text-xs italic text-neutral-400">Aucun billet → événement gratuit.</div>}
                  {(edit.tickets || []).map((t, idx) => (
                    <div key={t.id || idx} className="flex items-center gap-3 rounded-xl border border-neutral-100 bg-white p-2.5" data-testid={`event-edit-ticket-row-${idx}`}>
                      <div className="flex-1">
                        <div className="text-sm font-bold text-navy">{t.name} <span className="ml-2 rounded-full bg-neutral-100 px-2 py-0.5 text-[10px] font-normal text-neutral-500">{t.role}</span></div>
                        {t.description && <div className="text-xs text-neutral-600">{t.description}</div>}
                      </div>
                      <div className="font-bold text-wine-700">{t.price > 0 ? `${t.price} ${(edit.pricing?.currency || "eur").toUpperCase()}` : "Gratuit"}</div>
                      <button type="button" onClick={() => removeEditTicket(idx)} className="grid h-7 w-7 place-items-center rounded-full text-rose-500 hover:bg-rose-50" data-testid={`event-edit-ticket-remove-${idx}`}><Trash2 className="h-3.5 w-3.5" /></button>
                    </div>
                  ))}
                </div>
              </div>

            </div>
            <div className="mt-5 flex justify-end gap-3 border-t border-neutral-100 pt-4">
              <button onClick={() => setEdit(null)} className="rounded-full border border-neutral-200 px-5 py-2 text-sm font-semibold text-neutral-700 hover:bg-neutral-50">Annuler</button>
              <button onClick={saveEdit} className="rounded-full bg-wine-500 px-6 py-2 text-sm font-semibold text-white hover:bg-wine-600" data-testid="event-edit-save">Enregistrer</button>
            </div>
          </div>
        </div>
      )}
    </PortalLayout>
  );
}
